import { z } from 'zod';

export const CHECK_STATUSES = [
  'to_print',
  'printing',
  'outstanding',
  'cleared',
  'voided',
] as const;
export type CheckStatus = (typeof CHECK_STATUSES)[number];

/** Human labels for the register and the queue. */
export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
  to_print: 'To print',
  printing: 'Printing',
  outstanding: 'Outstanding',
  cleared: 'Cleared',
  voided: 'Voided',
};

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const positiveMoney = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a positive money value')
  .refine((v) => Number(v) > 0, 'must be greater than zero');

export const checkLineInputSchema = z.object({
  accountId: z.string().uuid(),
  amount: positiveMoney,
  memo: z.string().max(500).nullable().optional(),
  /**
   * Pay a liability that is ALREADY on the books instead of a fresh expense.
   * Points at the accrual credit line being settled — a paycheque's net pay
   * sitting in "Uncleared wage checks", say. The line's account must be that
   * accrual's account and the amount may not exceed what is still open, so
   * the same paycheque cannot be paid twice and wages are never expensed
   * twice. The uncashed-paycheques list clears the moment this posts.
   */
  settlesJournalLineId: z.string().uuid().nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
});
export type CheckLineInput = z.infer<typeof checkLineInputSchema>;

export const writeCheckInputSchema = z.object({
  bankAccountId: z.string().uuid(), // GL cash/bank account the check draws on
  /**
   * Omit to queue the check for batch printing — the print run assigns the
   * number. Supply one only when recording a check that was already written by
   * hand off-system, where the paper already carries a number.
   */
  checkNumber: z.string().trim().min(1).max(20).nullable().optional(),
  checkDate: isoDate,
  // The payee must be a contact in the system; name/address are snapshotted from it.
  payeeContactId: z.string().uuid(),
  memo: z.string().max(200).nullable().optional(),
  lines: z.array(checkLineInputSchema).min(1),
  /**
   * Set after the user confirms a "this looks like one you just wrote" warning.
   * Never set it by default — the warning is the only thing standing between a
   * mis-click and paying a vendor twice.
   */
  allowDuplicate: z.boolean().optional(),
});
export type WriteCheckInput = z.infer<typeof writeCheckInputSchema>;

export const voidCheckInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type VoidCheckInput = z.infer<typeof voidCheckInputSchema>;

export const listChecksQuerySchema = z.object({
  bankAccountId: z.string().uuid().optional(),
  status: z.enum(CHECK_STATUSES).optional(),
});
export type ListChecksQuery = z.infer<typeof listChecksQuerySchema>;

export interface Check {
  id: string;
  companyId: string;
  bankAccountId: string;
  /** NULL while queued for printing — the print run assigns it. */
  checkNumber: string | null;
  checkDate: string;
  payeeContactId: string | null;
  payeeName: string;
  payeeAddress: string | null;
  amount: string;
  memo: string | null;
  status: CheckStatus;
  journalEntryId: string | null;
  printRunId: string | null;
  printedAt: string | null;
  clearedAt: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CheckLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: string;
  memo: string | null;
  /** The accrual this line pays, when the check settles a liability. */
  settlesJournalLineId: string | null;
  settlesEntryNumber: string | null;
}

export interface CheckWithLines extends Check {
  bankAccountCode: string;
  bankAccountName: string;
  lines: CheckLine[];
}

export interface NextCheckNumber {
  checkNumber: string;
}
